import { Controller } from "@hotwired/stimulus";
import { formatNumberDisplay } from "./utils/number_format";

/**
 * remote-bind (Stimulus controller)
 *
 * But:
 * - rendre un JSON dans le DOM, sans templates JS complexes
 * - écouter l'event "remote:update:success" (émis par api-sync) qui bubble sur le conteneur
 *
 * Filtrage par channel:
 * - Optionnel: data-remote-bind-channel-value="listings_search"
 * - Si défini, seuls les events dont detail.channel matchent seront appliqués
 *
 * Attributs supportés (naming "nouveau", pas legacy):
 *
 * - Text/HTML:
 *   - [data-remote-text-path="meta.filters_summary"]
 *   - optionnel: [data-remote-format="currency:EUR"] / "percent:2" / "html"
 *
 * - Répétition (boucles):
 *   - Container: [data-remote-repeat-path="items"] (+ optionnel data-remote-repeat-mode="replace|append")
 *   - Champs dans le template: [data-remote-repeat-field="label"] (+ optionnel data-remote-format)
 *   - Attributs: [data-remote-repeat-attr="src:image_url,data-id:id"]
 *   - Dedupe (append): data-remote-repeat-key="id"
 *
 * - Styles:
 *   - [data-remote-style-path="capacity.debt_ratio_style"] où la valeur est un objet { left: "20%" }
 *   - Optionnel: [data-remote-style-show-on-set="true"] pour retirer la classe "d-none"
 *     dès qu'un style non-null est appliqué (utile pour cacher un curseur tant que le
 *     payload remote ne renvoie pas de valeur valide, cf. issue #151).
 *
 * - Cacher une section si tout est vide:
 *   - Container: [data-remote-hide-empty]
 *   - La section est cachée si tous les binds texte à l'intérieur valent "" ou "-"
 *
 * - Classes conditionnelles par signe:
 *   - [data-remote-class-by-sign="positive:text-success,negative:text-danger,zero:text-muted"]
 *   - Applique la classe correspondante selon le signe de la valeur (positif, négatif, zéro)
 *   - [data-remote-class-by-sign-path="scope.autre_champ"] pour lire le signe
 *     ailleurs que sur la valeur affichée (texte non numérique)
 */
export default class extends Controller {
  static values = {
    channel: String,
  };

  connect() {
    this.onUpdate = (e) => {
      try {
        if (this.hasChannelValue && this.channelValue) {
          const eventChannel = e?.detail?.channel;
          if (eventChannel !== this.channelValue) return;
        }
      } catch (_) {}
      this.applyAll(e.detail?.json);
    };
    this.element.addEventListener("remote:update:success", this.onUpdate);
  }

  disconnect() {
    this.element.removeEventListener("remote:update:success", this.onUpdate);
  }

  /**
   * Un `remote-bind` imbriqué est propriétaire de son sous-arbre : le contrôleur
   * parent ne doit pas toucher aux binds qui s'y trouvent. Sans cette règle, un
   * bloc branché sur son propre channel (ex. « Comparateur neuf / ancien »,
   * channel `new_vs_old`, rendu dans la colonne détails bindée sur
   * `listing_details`) verrait ses valeurs remises à « - » à chaque réponse du
   * channel parent, qui ne connaît pas ses chemins.
   * @param {Element} el
   * @returns {boolean}
   */
  owns(el) {
    const owner = el.closest('[data-controller~="remote-bind"]');
    return !owner || owner === this.element;
  }

  /**
   * Comme `querySelectorAll`, restreint aux éléments dont ce contrôleur est
   * propriétaire (cf. `owns`).
   * @param {string} selector
   * @returns {Element[]}
   */
  findAll(selector) {
    return Array.from(this.element.querySelectorAll(selector)).filter((el) => this.owns(el));
  }

  /**
   * Entry point: applique tous les types de binds à partir d'un JSON.
   * @param {object} json
   */
  applyAll(json) {
    if (!json) return;
    this.applyTextBinds(json);
    this.applyAttrBinds(json);
    this.applyTopLevelNestedRepeats(json);
    this.applyLoopContainers(json);
    this.applyStyleBindings(json);
    this.applyHideEmpty();
    this.applyAssignKeys(json);
  }

  // ---------- Text / HTML binds ----------
  /**
   * Remplit tous les [data-remote-text-path] avec la valeur correspondante.
   * Note: par défaut, null/undefined/"" → "-"
   */
  applyTextBinds(json) {
    const binds = this.findAll("[data-remote-text-path]");
    binds.forEach((el) => {
      const path = el.getAttribute("data-remote-text-path");
      if (!path) return;
      const raw = this.lookup(json, path);
      const fmt = el.getAttribute("data-remote-format") || "";
      const formatted = this.formatAccordingTo(fmt, raw);
      this.setElementText(el, formatted);
      this.applyClassBySign(el, raw, json);
    });
  }

  // ---------- Attribute binds ----------
  /**
   * Remplit des attributs HTML depuis le JSON.
   * Usage: data-remote-attr-path="src:listing_info.image_url,alt:listing_info.city"
   * - Pour chaque paire "attrName:json.path", lit la valeur dans le JSON et
   *   l'écrit sur l'attribut correspondant (ou le retire si la valeur est nulle).
   * - Pendant que la valeur est nulle/vide, l'attribut est retiré de l'élément
   *   (utile pour qu'une <img> sans src ne déclenche pas un fallback erreur visible).
   */
  applyAttrBinds(json) {
    const targets = this.findAll("[data-remote-attr-path]");
    targets.forEach((el) => {
      const spec = el.getAttribute("data-remote-attr-path") || "";
      const pairs = spec.split(",").map((s) => s.trim()).filter(Boolean);
      pairs.forEach((pair) => {
        const idx = pair.indexOf(":");
        if (idx <= 0) return;
        const attrName = pair.slice(0, idx).trim();
        const path = pair.slice(idx + 1).trim();
        if (!attrName || !path) return;
        const value = this.lookup(json, path);
        try {
          if (value == null || value === "") {
            el.removeAttribute(attrName);
          } else {
            el.setAttribute(attrName, String(value));
          }
        } catch (_) {}
      });
    });
  }

  // ---------- Loop containers ----------
  /**
   * Rend des listes à partir de containers [data-remote-repeat-path].
   * Le HTML initial du container sert de template.
   *
   * Important:
   * - mode "append" garde le contenu existant et ajoute des items; avec key, on déduplique.
   */
  applyLoopContainers(json) {
    const containers = this.findAll("[data-remote-repeat-path]");
    containers.forEach((container) => {
      const arrayPath = container.getAttribute("data-remote-repeat-path");
      if (!arrayPath) return;
      const items = this.lookup(json, arrayPath);
      const mode = (container.getAttribute("data-remote-repeat-mode") || "replace").toLowerCase();
      const keyField = container.getAttribute("data-remote-repeat-key") || "";
      if (!container.dataset.template) {
        container.dataset.template = container.innerHTML;
      }
      if (mode === "replace") {
        container.innerHTML = "";
      } else if (mode === "append" && container.innerHTML.trim() === container.dataset.template.trim()) {
        // First render in append mode: clear initial template.
        container.innerHTML = "";
      }
      if (!Array.isArray(items) || items.length === 0) return;
      const template = container.dataset.template;

      // Optional dedupe when appending
      const existingKeys = new Set();
      if (mode === "append" && keyField) {
        Array.from(container.querySelectorAll("[data-remote-repeat-item-key]")).forEach((el) => {
          const k = el.getAttribute("data-remote-repeat-item-key");
          if (k) existingKeys.add(k);
        });
      }

      items.forEach((item) => {
        if (mode === "append" && keyField) {
          const k = this.lookup(item, keyField);
          const ks = (k == null) ? "" : String(k);
          if (!ks || existingKeys.has(ks)) return;
          existingKeys.add(ks);
        }
        // Use appropriate wrapper for table elements (tr, td, etc.)
        const wrapper = this.createTemplateWrapper(template, container);
        
        // Process nested repeats within this item (must be done before regular field binds)
        this.applyNestedRepeats(wrapper, item);
        
        const binds = Array.from(wrapper.querySelectorAll("[data-remote-repeat-field]"));
        binds.forEach((el) => {
          const relPathRaw = el.getAttribute("data-remote-repeat-field") || "";
          const relPath = relPathRaw.replace(/^[^.]*\./, "");
          let value;
          if (relPath === "offset_plus_duration") {
            const months = Number(item.offset || 0) + Number(item.duration || 0);
            value = months / 12.0;
          } else {
            value = this.lookup(item, relPath);
          }
          const hideZero = el.hasAttribute("data-remote-hide-zero");
          if (hideZero && (Number(value) === 0)) {
            this.setElementText(el, "-");
          } else {
            const fmt = el.getAttribute("data-remote-format") || "";
            const formatted = this.formatAccordingTo(fmt, value);
            this.setElementText(el, formatted);
          }
          this.applyClassBySign(el, value);
        });

        // Attributes in repeat templates
        // Usage:
        //   data-remote-repeat-attr="src:image_url,data-listing-id:id"
        const attrTargets = Array.from(wrapper.querySelectorAll("[data-remote-repeat-attr]"));
        attrTargets.forEach((el) => {
          const spec = el.getAttribute("data-remote-repeat-attr") || "";
          const pairs = spec
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          pairs.forEach((pair) => {
            const idx = pair.indexOf(":");
            if (idx <= 0) return;
            const attrName = pair.slice(0, idx).trim();
            const relPathRaw = pair.slice(idx + 1).trim();
            const relPath = relPathRaw.replace(/^[^.]*\./, "");
            const v = this.lookup(item, relPath);
            const vs = v == null ? "" : String(v);
            try { el.setAttribute(attrName, vs); } catch (_) {}
          });
        });

        // Move nodes to container; annotate first element with key when present
        const nodes = [];
        while (wrapper.firstChild) {
          const n = wrapper.firstChild;
          nodes.push(n);
          // Important: remove from wrapper to avoid infinite loop
          try { n.remove(); } catch (_) { try { wrapper.removeChild(n); } catch (_e) {} }
        }
        if (mode === "append" && keyField) {
          const k = this.lookup(item, keyField);
          const ks = (k == null) ? "" : String(k);
          const firstEl = nodes.find((n) => n && n.nodeType === 1);
          if (firstEl && ks) {
            try { firstEl.setAttribute("data-remote-repeat-item-key", ks); } catch (_) {}
          }
        }
        nodes.forEach((n) => container.appendChild(n));
      });
    });
  }

  // ---------- Top-level nested repeats ----------
  /**
   * Traite les nested repeats qui ne sont PAS à l'intérieur d'un data-remote-repeat-path.
   * Cela permet d'avoir des éléments répétés horizontalement (ex: colonnes de header)
   * sans être dans une boucle parente.
   * 
   * @param {object} json - Les données JSON complètes
   */
  applyTopLevelNestedRepeats(json) {
    // Reset: re-inject stored template so we can re-apply with new data (e.g. after loading another listing)
    const parentsWithStoredTemplate = this.findAll("[data-remote-nested-repeat-template]");
    parentsWithStoredTemplate.forEach((parent) => {
      const templateHTML = parent.getAttribute("data-remote-nested-repeat-template");
      if (!templateHTML) return;
      Array.from(parent.querySelectorAll("[data-remote-nested-repeat-generated]")).forEach((child) => child.remove());
      Array.from(parent.querySelectorAll("[data-remote-nested-repeat-path]")).forEach((child) => child.remove());
      const restored = this.createTemplateWrapper(templateHTML, parent).firstElementChild;
      if (restored) this.insertNestedRepeatNode(parent, restored);
    });

    // Find all nested-repeat elements that are NOT inside a repeat-path container
    const allNestedRepeats = this.findAll("[data-remote-nested-repeat-path]");
    const topLevelNestedRepeats = allNestedRepeats.filter((el) => {
      // Check if this element is inside a data-remote-repeat-path container
      let parent = el.parentElement;
      while (parent && parent !== this.element) {
        if (parent.hasAttribute("data-remote-repeat-path")) {
          return false; // Inside a repeat container, will be handled by applyNestedRepeats
        }
        parent = parent.parentElement;
      }
      return true; // Top-level, handle here
    });

    topLevelNestedRepeats.forEach((templateEl) => {
      const nestedPath = templateEl.getAttribute("data-remote-nested-repeat-path");
      const nestedItems = this.lookup(json, nestedPath);
      if (!Array.isArray(nestedItems) || nestedItems.length === 0) return;

      // Store template and get parent
      const parent = templateEl.parentNode;
      const templateHTML = templateEl.outerHTML;

      this.markNestedRepeatAnchor(parent, templateEl);

      // Store template on parent so we can re-apply when new data arrives
      parent.setAttribute("data-remote-nested-repeat-template", templateHTML);

      // Remove original template element
      templateEl.remove();

      // Create clones for each nested item
      nestedItems.forEach((nestedItem) => {
        const clone = this.createTemplateWrapper(templateHTML, parent).firstElementChild;
        if (!clone) return;

        // Remove the nested-repeat-path attribute from clone
        clone.removeAttribute("data-remote-nested-repeat-path");

        // Mark clone so we can remove it on next run before re-injecting template
        clone.setAttribute("data-remote-nested-repeat-generated", "true");

        // Fill in data-remote-repeat-field on the clone itself (or use item directly if no field)
        const field = clone.getAttribute("data-remote-repeat-field");
        let value;
        if (field) {
          // For top-level, nestedItem can be a string or an object
          value = typeof nestedItem === "object" ? this.lookup(nestedItem, field) : nestedItem;
          clone.removeAttribute("data-remote-repeat-field");
        } else if (typeof nestedItem !== "object") {
          // No field specified and item is a primitive - use item directly
          value = nestedItem;
        }
        
        if (value !== undefined) {
          const fmt = clone.getAttribute("data-remote-format") || "";
          const formatted = this.formatAccordingTo(fmt, value);
          this.setElementText(clone, formatted);
          this.applyClassBySign(clone, value);
        }

        // Also check for nested fields inside the clone
        const innerBinds = Array.from(clone.querySelectorAll("[data-remote-repeat-field]"));
        innerBinds.forEach((el) => {
          const relPath = el.getAttribute("data-remote-repeat-field");
          const innerValue = typeof nestedItem === "object" ? this.lookup(nestedItem, relPath) : nestedItem;
          const fmt = el.getAttribute("data-remote-format") || "";
          const formatted = this.formatAccordingTo(fmt, innerValue);
          this.setElementText(el, formatted);
          this.applyClassBySign(el, innerValue);
          el.removeAttribute("data-remote-repeat-field");
        });

        this.insertNestedRepeatNode(parent, clone);
      });
    });
  }

  // ---------- Nested repeats ----------
  /**
   * Traite les boucles imbriquées dans un template.
   * Usage: data-remote-nested-repeat-path="values" sur un élément qui sera répété
   * pour chaque item du tableau à ce chemin dans itemData.
   * 
   * @param {Element} wrapper - Le wrapper contenant le template parsé
   * @param {object} itemData - Les données de l'item parent
   */
  applyNestedRepeats(wrapper, itemData) {
    const nestedContainers = Array.from(wrapper.querySelectorAll("[data-remote-nested-repeat-path]"));
    nestedContainers.forEach((templateEl) => {
      const nestedPath = templateEl.getAttribute("data-remote-nested-repeat-path");
      const nestedItems = this.lookup(itemData, nestedPath);
      if (!Array.isArray(nestedItems) || nestedItems.length === 0) return;

      // Store template and get parent
      const parent = templateEl.parentNode;
      const templateHTML = templateEl.outerHTML;

      this.markNestedRepeatAnchor(parent, templateEl);

      // Remove original template element
      templateEl.remove();

      // Create clones for each nested item
      nestedItems.forEach((nestedItem) => {
        const clone = this.createTemplateWrapper(templateHTML, parent).firstElementChild;
        if (!clone) return;

        // Remove the nested-repeat-path attribute from clone (it's been processed)
        clone.removeAttribute("data-remote-nested-repeat-path");

        // Fill in data-remote-repeat-field on the clone itself
        const field = clone.getAttribute("data-remote-repeat-field");
        if (field) {
          const value = this.lookup(nestedItem, field);
          const fmt = clone.getAttribute("data-remote-format") || "";
          const formatted = this.formatAccordingTo(fmt, value);
          this.setElementText(clone, formatted);
          this.applyClassBySign(clone, value);
          // Remove the field attribute to prevent re-processing by parent loop
          clone.removeAttribute("data-remote-repeat-field");
        }

        // Also check for nested fields inside the clone
        const innerBinds = Array.from(clone.querySelectorAll("[data-remote-repeat-field]"));
        innerBinds.forEach((el) => {
          const relPath = el.getAttribute("data-remote-repeat-field");
          const value = this.lookup(nestedItem, relPath);
          const fmt = el.getAttribute("data-remote-format") || "";
          const formatted = this.formatAccordingTo(fmt, value);
          this.setElementText(el, formatted);
          this.applyClassBySign(el, value);
          // Remove the field attribute to prevent re-processing by parent loop
          el.removeAttribute("data-remote-repeat-field");
        });

        this.insertNestedRepeatNode(parent, clone);
      });
    });
  }

  // ---------- Style binds ----------
  /**
   * Applique des styles depuis le JSON.
   * - Si la valeur est un objet {prop: value}, on fait el.style.setProperty
   * - Si la valeur est une string, on fait el.setAttribute("style", value)
   */
  applyStyleBindings(json) {
    const explicitTargets = this.findAll("[data-remote-style-path]");
    explicitTargets.forEach((el) => {
      const path = el.getAttribute("data-remote-style-path");
      if (!path) return;
      const styleDef = this.lookup(json, path);
      if (styleDef == null) return;
      this.setElementStyle(el, styleDef);
      if (el.getAttribute("data-remote-style-show-on-set") === "true") {
        el.classList.remove("d-none");
      }
    });
  }

  // ---------- Hide empty sections ----------
  /**
   * Cache les containers [data-remote-hide-empty] si tous les binds texte
   * à l'intérieur sont vides (""/"-").
   */
  applyHideEmpty() {
    const containers = this.findAll("[data-remote-hide-empty]");
    containers.forEach((container) => {
      const selector = "[data-remote-text-path],[data-remote-repeat-field]";
      const binds = [];
      // querySelectorAll ne matche pas le container lui-même
      if (container.matches(selector)) binds.push(container);
      binds.push(...Array.from(container.querySelectorAll(selector)));
      if (binds.length === 0) return;
      const allBlank = binds.every((b) => {
        const content = (b.textContent || "").trim();
        return content === "" || content === "-";
      });
      container.classList.toggle("d-none", allBlank);
    });
  }

  // ---------- Assign keys ----------
  /**
   * Assigne des valeurs du JSON à des variables globales (window[key]) pour usage par scripts inline.
   * Usage: data-remote-assign-key="variableName" data-remote-assign-value-path="path.to.value"
   * (path optionnel : si absent, assigne le json entier)
   */
  applyAssignKeys(json) {
    const assignElements = this.findAll("[data-remote-assign-key]");
    assignElements.forEach((el) => {
      const key = el.getAttribute("data-remote-assign-key");
      const path = el.getAttribute("data-remote-assign-value-path");
      if (!key) return;
      const value = path ? this.lookup(json, path) : json;
      try {
        window[key] = value;
      } catch (e) {
        console.warn(`Assign key '${key}' error:`, e);
      }
    });
  }

  // ---------- Helpers (ported) ----------

  /**
   * Creates an appropriate wrapper element for parsing HTML template.
   * Table elements (tr, td, th, thead, tbody, tfoot) need to be parsed
   * within a table context, otherwise the browser won't create them correctly.
   * @param {string} template - The HTML template string
   * @param {Element} container - The container element (to detect table context)
   * @returns {Element} - A wrapper element with innerHTML set to template
   */
  /**
   * Marque le sibling suivant du template comme point d'insertion pour les clones
   * (préserve l'ordre des colonnes, ex. avant "Annuité TTC").
   */
  markNestedRepeatAnchor(parent, templateEl) {
    if (parent.querySelector("[data-remote-nested-repeat-anchor]")) return;
    const anchor = templateEl.nextElementSibling;
    if (anchor) anchor.setAttribute("data-remote-nested-repeat-anchor", "true");
  }

  /**
   * Insère un nœud généré par nested-repeat à la position d'ancrage mémorisée.
   */
  insertNestedRepeatNode(parent, node) {
    const anchor = parent.querySelector("[data-remote-nested-repeat-anchor]");
    if (anchor) {
      parent.insertBefore(node, anchor);
    } else {
      parent.appendChild(node);
    }
  }

  createTemplateWrapper(template, container) {
    const trimmed = template.trim();
    const tagName = container.tagName.toLowerCase();
    
    // Check if we're in a table context or if template starts with table elements
    const isTableRow = trimmed.startsWith("<tr") || tagName === "tbody" || tagName === "thead" || tagName === "tfoot";
    const isTableCell = trimmed.startsWith("<td") || trimmed.startsWith("<th") || tagName === "tr";
    
    if (isTableRow) {
      // Wrap in table > tbody for proper parsing of <tr> elements
      const table = document.createElement("table");
      const tbody = document.createElement("tbody");
      tbody.innerHTML = template;
      table.appendChild(tbody);
      return tbody;
    } else if (isTableCell) {
      // Wrap in table > tbody > tr for proper parsing of <td>/<th> elements
      const table = document.createElement("table");
      const tbody = document.createElement("tbody");
      const tr = document.createElement("tr");
      tr.innerHTML = template;
      tbody.appendChild(tr);
      table.appendChild(tbody);
      return tr;
    } else {
      // Default: use a div wrapper
      const wrapper = document.createElement("div");
      wrapper.innerHTML = template;
      return wrapper;
    }
  }

  /**
   * Lookup "dotted path" dans un objet.
   * Supporte "a[0].b" ou "a[0][b]" via normalisation basique.
   */
  lookup(obj, path) {
    if (!obj || !path) return null;
    const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return null;
      cur = cur[p];
    }
    return cur == null ? null : cur;
  }

  /**
   * Formate une valeur selon un token (ex: "currency:EUR", "percent:2", "html").
   * - Retourne soit une string/number, soit un objet { html: "..."} pour innerHTML.
   */
  formatAccordingTo(fmtToken, value) {
    const fmt = fmtToken || "";
    const [kind, arg] = fmt.split(":");
    const kindTrimmed = (kind || "").trim();
    if (kindTrimmed !== "html" && kindTrimmed !== "json" && (value === null || value === undefined || value === "")) return "-";
    switch (kindTrimmed) {
      case "html": {
        return { html: value == null ? "" : String(value) };
      }
      case "json": {
        if (value == null) return "";
        try {
          return JSON.stringify(value, null, 2);
        } catch (_) {
          return String(value);
        }
      }
      case "currency": {
        return formatNumberDisplay(value, { decimals: 0, unit: "€" });
      }
      case "currency_by_month": {
        return formatNumberDisplay(value, { decimals: 0, unit: "€/mois" });
      }
      case "percent": {
        const decimals = arg ? Number(arg) : 0;
        return formatNumberDisplay(value, { decimals, unit: "%" });
      }
      case "integer": {
        return formatNumberDisplay(value, { decimals: 0 });
      }
      case "decimal": {
        const decimals = arg ? Number(arg) : 1;
        return formatNumberDisplay(value, { decimals });
      }
      case "sqm": {
        return formatNumberDisplay(value, { decimals: 2, unit: "m\u00B2" });
      }
      case "sqm_or_no": {
        const n = Number(value);
        if (Number.isNaN(n) || n <= 0) return "Non";
        return formatNumberDisplay(n, { decimals: 2, unit: "m\u00B2" });
      }
      case "count_or_no": {
        const n = Number(value);
        if (Number.isNaN(n) || n <= 0) return "Non";
        return formatNumberDisplay(n, { decimals: 0 });
      }
      case "ans": {
        const n = Number(value);
        if (Number.isNaN(n)) return "-";
        const v = Math.round(n);
        const unit = v === 1 ? "an" : "ans";
        return `${formatNumberDisplay(v, { decimals: 0 })} ${unit}`;
      }
      case "mois": {
        const n = Number(value);
        if (Number.isNaN(n)) return "-";
        const v = Math.round(n);
        return `${formatNumberDisplay(v, { decimals: 0 })} mois`;
      }
      default: {
        return value;
      }
    }
  }

  /**
   * Écrit du texte ou du HTML dans un élément.
   * @param {Element} el
   * @param {string|number|{html: string}} text
   */
  setElementText(el, text) {
    if (text && typeof text === "object" && Object.prototype.hasOwnProperty.call(text, "html")) {
      el.innerHTML = text.html;
      return;
    }
    el.textContent = text == null ? "-" : String(text);
  }

  /**
   * Applique un style (objet ou string) sur un élément.
   */
  setElementStyle(el, styleDef) {
    try {
      if (styleDef && typeof styleDef === "object" && !Array.isArray(styleDef)) {
        Object.entries(styleDef).forEach(([prop, val]) => {
          if (val == null || val === "") {
            el.style.removeProperty(prop);
          } else {
            el.style.setProperty(prop, String(val));
          }
        });
      } else if (typeof styleDef === "string") {
        el.setAttribute("style", styleDef);
      }
    } catch (_) {
      // no-op
    }
  }

  /**
   * Applique des classes CSS conditionnelles selon le signe de la valeur.
   * Usage: data-remote-class-by-sign="positive:text-success,negative:text-danger,zero:text-muted"
   *
   * Le signe est lu sur la valeur affichée par l'élément, sauf si
   * `data-remote-class-by-sign-path` désigne un autre chemin du payload : utile
   * quand le texte est une phrase composée côté serveur, dont on ne peut pas
   * déduire le signe (ex. « 25 500 € moins cher »).
   *
   * @param {Element} el - L'élément DOM
   * @param {*} rawValue - La valeur brute (avant formatage)
   * @param {Object} [json] - Payload complet, requis par `...-by-sign-path`
   */
  applyClassBySign(el, rawValue, json) {
    const spec = el.getAttribute("data-remote-class-by-sign");
    if (!spec) return;

    const signPath = el.getAttribute("data-remote-class-by-sign-path");
    const value = signPath && json ? this.lookup(json, signPath) : rawValue;

    // Parse le mapping: "positive:class1,negative:class2,zero:class3"
    const mapping = {};
    spec.split(",").forEach((pair) => {
      const idx = pair.indexOf(":");
      if (idx <= 0) return;
      const sign = pair.slice(0, idx).trim().toLowerCase();
      const className = pair.slice(idx + 1).trim();
      if (sign && className) {
        mapping[sign] = className;
      }
    });

    // Détermine le signe de la valeur
    const num = Number(value);
    let sign;
    if (Number.isNaN(num) || value === null || value === undefined || value === "") {
      sign = "zero"; // Traite les valeurs invalides comme zéro
    } else if (num > 0) {
      sign = "positive";
    } else if (num < 0) {
      sign = "negative";
    } else {
      sign = "zero";
    }

    // Retire toutes les classes définies dans le mapping, puis applique la bonne
    Object.values(mapping).forEach((cls) => {
      if (cls) el.classList.remove(cls);
    });
    const targetClass = mapping[sign];
    if (targetClass) {
      el.classList.add(targetClass);
    }
  }
}